import React from "react";
import { toast } from "sonner";
import { Loader2, Plus, MapPin, Trash2, Pencil, Save, X } from "lucide-react";
import { businessApi } from "../../lib/api";

const empty = { name: "", address: "", postal_code: "", city: "", contact_person: "", phone: "", email: "", opening_hours: "", pickup_instructions: "", default_product: "same_day_b2b", active: true };

export default function BusinessPickupLocationsPage() {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [editing, setEditing] = React.useState(null); // null | new | item

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await businessApi().get("/business/pickup-locations");
      setItems(data.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Kunne ikke hente lokationer"); }
    setLoading(false);
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const save = async (form) => {
    try {
      if (form.id) await businessApi().patch(`/business/pickup-locations/${form.id}`, form);
      else await businessApi().post("/business/pickup-locations", form);
      toast.success("Gemt");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gemning fejlede"); }
  };

  const del = async (id) => {
    if (!window.confirm("Slet denne lokation?")) return;
    try {
      await businessApi().delete(`/business/pickup-locations/${id}`);
      toast.success("Slettet");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sletning fejlede"); }
  };

  return (
    <div className="space-y-6" data-testid="business-pickup-locations-page">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-brand-red font-semibold">Pickup-lokationer</div>
          <h1 className="font-display font-bold text-3xl text-brand-dark mt-1">Dine afhentnings-steder</h1>
          <p className="text-sm text-brand-text2 mt-1">Tilføj alle lagre/butikker hvor EMA henter pakker.</p>
        </div>
        <button onClick={() => setEditing({ ...empty })} className="btn-primary ml-auto" data-testid="biz-pickup-new">
          <Plus size={16}/> Ny lokation
        </button>
      </div>

      {loading && <div className="text-brand-text2 flex items-center gap-2"><Loader2 className="animate-spin" size={14}/> Indlæser...</div>}

      {!loading && items.length === 0 && (
        <div className="bg-white border border-brand p-8 text-center text-brand-text2" data-testid="biz-pickup-empty">
          <MapPin size={32} className="mx-auto mb-2 text-brand-text2/60"/>
          Ingen pickup-lokationer endnu. Klik &quot;Ny lokation&quot; for at oprette den første.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((l) => (
          <div key={l.id} className="bg-white border border-brand p-4 space-y-2" data-testid={`biz-pickup-card-${l.id}`}>
            <div className="flex items-start gap-2">
              <MapPin size={18} className="text-brand-red mt-0.5 flex-shrink-0"/>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-brand-dark">{l.name}</div>
                <div className="text-sm text-brand-text2">{l.address}, {l.postal_code} {l.city}</div>
              </div>
              <span className={`text-[10px] px-1.5 py-0.5 ${l.active ? "bg-emerald-100 text-emerald-800" : "bg-gray-100 text-gray-700"}`}>
                {l.active ? "Aktiv" : "Inaktiv"}
              </span>
            </div>
            <div className="text-xs text-brand-text2 space-y-0.5">
              {l.contact_person && <div>👤 {l.contact_person}</div>}
              {l.phone && <div>📞 {l.phone}</div>}
              {l.email && <div>✉️ {l.email}</div>}
              {l.opening_hours && <div>🕒 {l.opening_hours}</div>}
              {l.pickup_instructions && <div className="italic">&quot;{l.pickup_instructions}&quot;</div>}
            </div>
            <div className="flex gap-2 text-xs pt-2 border-t border-gray-100">
              <button onClick={() => setEditing(l)} className="text-blue-700 underline inline-flex items-center gap-1" data-testid={`biz-pickup-edit-${l.id}`}><Pencil size={12}/> Redigér</button>
              <button onClick={() => del(l.id)} className="text-red-600 underline inline-flex items-center gap-1 ml-auto" data-testid={`biz-pickup-delete-${l.id}`}><Trash2 size={12}/> Slet</button>
            </div>
          </div>
        ))}
      </div>

      {editing && <LocationModal initial={editing} onClose={() => setEditing(null)} onSave={save}/>}
    </div>
  );
}

function LocationModal({ initial, onClose, onSave }) {
  const [f, setF] = React.useState(initial);
  const [busy, setBusy] = React.useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await onSave(f); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center px-4 z-50" data-testid="biz-pickup-modal">
      <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-bold text-2xl text-brand-dark">{f.id ? "Redigér lokation" : "Ny pickup-lokation"}</h2>
          <button onClick={onClose}><X size={20}/></button>
        </div>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <Field label="Navn (intern)" required value={f.name} onChange={(v) => set("name", v)} testid="biz-pickup-name"/>
          <Field label="Kontakt-person" value={f.contact_person} onChange={(v) => set("contact_person", v)} testid="biz-pickup-contact"/>
          <Field label="Adresse" required value={f.address} onChange={(v) => set("address", v)} testid="biz-pickup-address" cols={2}/>
          <Field label="Postnr" required value={f.postal_code} onChange={(v) => set("postal_code", v)} testid="biz-pickup-postal"/>
          <Field label="By" required value={f.city} onChange={(v) => set("city", v)} testid="biz-pickup-city"/>
          <Field label="Telefon" value={f.phone} onChange={(v) => set("phone", v)} testid="biz-pickup-phone"/>
          <Field label="Email" type="email" value={f.email} onChange={(v) => set("email", v)} testid="biz-pickup-email"/>
          <Field label="Åbningstider" value={f.opening_hours} onChange={(v) => set("opening_hours", v)} testid="biz-pickup-hours" cols={2}/>
          <div className="md:col-span-2">
            <label className="label-flat">Pickup-instruktioner (synlige for chauffør)</label>
            <textarea className="input-flat" rows={2} value={f.pickup_instructions} onChange={(e) => set("pickup_instructions", e.target.value)} data-testid="biz-pickup-instructions"/>
          </div>
          <div>
            <label className="label-flat">Standard service-niveau</label>
            <select className="input-flat" value={f.default_product} onChange={(e) => set("default_product", e.target.value)} data-testid="biz-pickup-default-product">
              <option value="same_day_b2b">Same Day Business</option>
              <option value="next_day_b2b">Next Day Business</option>
            </select>
          </div>
          <label className="text-sm flex items-end gap-2 pb-2">
            <input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} data-testid="biz-pickup-active"/>
            Aktiv
          </label>
          <div className="md:col-span-2 flex gap-2 mt-3">
            <button type="button" onClick={onClose} className="btn-secondary">Annullér</button>
            <button type="submit" disabled={busy} className="btn-primary ml-auto" data-testid="biz-pickup-save">
              {busy ? <Loader2 className="animate-spin" size={14}/> : <Save size={14}/>} Gem
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, required, type = "text", cols, testid }) {
  return (
    <div className={cols === 2 ? "md:col-span-2" : ""}>
      <label className="label-flat">{label}{required && " *"}</label>
      <input className="input-flat" type={type} required={!!required} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}/>
    </div>
  );
}
